import FluxUtils from 'flux/utils';
import Immutable from 'immutable';
import moment from 'moment';

import asFunctional from 'sharedreact/components/asFunctional';
import SettingsStore from 'sharedreact/stores/SettingsStore';
import { assertNonNullish } from 'sharedreact/utils/TypeSupport';

import LoadHistoryActions from 'workspaces/launcher/actions/LoadHistoryActions';
import LoadHistoryTab from 'workspaces/launcher/components/admin/LoadHistoryTab';
import LoadHistoryDataType from 'workspaces/launcher/constants/LoadHistoryDataType';
import LoadHistoryRange from 'workspaces/launcher/constants/LoadHistoryRange';
import LoadHistoryStore from 'workspaces/launcher/stores/LoadHistoryStore';

type Props = {
  index: number,
  activeTab: number,
  language: string
};

type State = ConstructorParameters<typeof LoadHistoryTab>[0];

function getStores () {
  return [
    SettingsStore,
    LoadHistoryStore
  ];
}

function handleLeave (): void {
  // cleanup storage in LoadHistoryStore
  LoadHistoryActions.clearAll();
}

function handleZoomIn (): void {
  // zoom in to half of the current time interval
  let [viewportStart, viewportEnd]: number[] = LoadHistoryStore.getCurrentViewport();
  if (viewportStart && viewportEnd) {
    viewportStart = viewportStart + (viewportEnd - viewportStart) / 2;
    const recordingEnd: number = LoadHistoryStore.getRecordingEnd();
    if (recordingEnd === viewportEnd) viewportEnd = 0;            // will adapt endpoint in case new data was logged
    const maxSamples: number = LoadHistoryStore.getMaxSamples();

    LoadHistoryActions.fetch(LoadHistoryDataType.USER_DATA, viewportStart, viewportEnd, maxSamples);
    LoadHistoryActions.fetch(LoadHistoryDataType.QUEUE_DATA, viewportStart, viewportEnd, maxSamples);
  }
}

function handleZoomOut (): void {
  // zoom out to twice the current time interval
  const [viewportStart, viewportEnd]: number[] = LoadHistoryStore.getCurrentViewport();
  if (viewportStart && viewportEnd) {
    const [recordingStart, recordingEnd]: number[] = LoadHistoryStore.getRecordingInterval();

    let newStart = Math.max(recordingStart, viewportStart - (viewportEnd - viewportStart));
    let newEnd = Math.min(recordingEnd, newStart + 2 * (viewportEnd - viewportStart));
    if (newStart === recordingStart) newStart = 0;
    if (newEnd === recordingEnd) newEnd = 0;
    const maxSamples: number = LoadHistoryStore.getMaxSamples();

    LoadHistoryActions.fetch(LoadHistoryDataType.USER_DATA, newStart, newEnd, maxSamples);
    LoadHistoryActions.fetch(LoadHistoryDataType.QUEUE_DATA, newStart, newEnd, maxSamples);
  }
}

function handleGoBackward (): void {
  const [viewportStart, viewportEnd]: number[] = LoadHistoryStore.getRequestedViewport();
  if (viewportStart && viewportEnd) {
    const recordingStart: number = LoadHistoryStore.getRecordingStart();

    let newStart = Math.max(recordingStart, viewportStart - (viewportEnd - viewportStart));
    let newEnd = newStart + (viewportEnd - viewportStart);
    if (newStart === recordingStart) newStart = 0;
    const maxSamples: number = LoadHistoryStore.getMaxSamples();

    LoadHistoryActions.fetch(LoadHistoryDataType.USER_DATA, newStart, newEnd, maxSamples);
    LoadHistoryActions.fetch(LoadHistoryDataType.QUEUE_DATA, newStart, newEnd, maxSamples);
  }
}

function handleGoFastBackward (): void {
  const [viewportStart, viewportEnd]: number[] = LoadHistoryStore.getRequestedViewport();
  if (viewportStart && viewportEnd) {
    const recordingStart: number = LoadHistoryStore.getRecordingStart();

    let newEnd = recordingStart + (viewportEnd - viewportStart);
    const maxSamples: number = LoadHistoryStore.getMaxSamples();
    
    LoadHistoryActions.fetch(LoadHistoryDataType.USER_DATA, 0, newEnd, maxSamples);
    LoadHistoryActions.fetch(LoadHistoryDataType.QUEUE_DATA, 0, newEnd, maxSamples);
  }
}

function handleGoForward (): void {
  const [viewportStart, viewportEnd]: number[] = LoadHistoryStore.getRequestedViewport();
  if (viewportStart && viewportEnd) {
    const recordingEnd: number = LoadHistoryStore.getRecordingEnd();

    let newEnd = Math.min(recordingEnd, viewportEnd + (viewportEnd - viewportStart));
    let newStart = newEnd - (viewportEnd - viewportStart);
    if (newEnd === recordingEnd) newEnd = 0;
    const maxSamples: number = LoadHistoryStore.getMaxSamples();

    LoadHistoryActions.fetch(LoadHistoryDataType.USER_DATA, newStart, newEnd, maxSamples);
    LoadHistoryActions.fetch(LoadHistoryDataType.QUEUE_DATA, newStart, newEnd, maxSamples);
  }
}

function handleGoFastForward (): void {
  const [viewportStart, viewportEnd]: number[] = LoadHistoryStore.getRequestedViewport();
  if (viewportStart && viewportEnd) {
    const recordingEnd: number = LoadHistoryStore.getRecordingEnd();

    const newStart = recordingEnd - (viewportEnd - viewportStart);
    const maxSamples: number = LoadHistoryStore.getMaxSamples();

    LoadHistoryActions.fetch(LoadHistoryDataType.USER_DATA, newStart, 0, maxSamples);
    LoadHistoryActions.fetch(LoadHistoryDataType.QUEUE_DATA, newStart, 0, maxSamples);
  }
}

function _substractRange (endTime: moment.Moment, range: string): number {
  switch (range) {
    case LoadHistoryRange.RANGE_LASTHOUR: {
      return endTime.subtract(1, 'hour').valueOf();
    }
    case LoadHistoryRange.RANGE_LASTDAY: {
      return endTime.subtract(1, 'day').valueOf();
    }
    case LoadHistoryRange.RANGE_LASTWEEK: {
      return endTime.subtract(1, 'week').valueOf();
    }
    case LoadHistoryRange.RANGE_LASTMONTH: {
      return endTime.subtract(1, 'month').valueOf();
    }
    case LoadHistoryRange.RANGE_LAST3MONTH: {
      return endTime.subtract(3, 'month').valueOf();
    }
    case LoadHistoryRange.RANGE_LAST6MONTH: {
      return endTime.subtract(6, 'month').valueOf();
    }
    case LoadHistoryRange.RANGE_LASTYEAR: {
      return endTime.subtract(1, 'year').valueOf();
    }
    case LoadHistoryRange.RANGE_ALL: {
      return 0;
    }
    default: {
      // unknown range
      console.log('Unknown range !!!');
      return -1;
    }
  }
}

function handleSelectRange (range: string): void {
  const recordingEnd: number = LoadHistoryStore.getRecordingEnd();

  if (recordingEnd) {
    const endTime: moment.Moment = moment(recordingEnd);

    const startTime: number = _substractRange(endTime, range);
    if (startTime < 0) return;        // invalid range

    LoadHistoryActions.fetchRange(LoadHistoryDataType.USER_DATA, startTime, range, LoadHistoryStore.getMaxSamples());
    LoadHistoryActions.fetchRange(LoadHistoryDataType.QUEUE_DATA, startTime, range, LoadHistoryStore.getMaxSamples());
  }
}

function calculateState (prevState?: State, props?: Props): State {
  assertNonNullish(props);

  const canZoomIn = LoadHistoryStore.canZoomIn();
  const canZoomOut = LoadHistoryStore.canZoomOut();
  const canGoBackward = LoadHistoryStore.canGoBackward();
  const canGoForward = LoadHistoryStore.canGoForward();

  /* no longer offer last day in range selector as rollover of gzip files is 24 hrs
  const allRanges = [LoadHistoryRange.RANGE_LASTDAY,
    LoadHistoryRange.RANGE_LASTWEEK, LoadHistoryRange.RANGE_LASTMONTH, LoadHistoryRange.RANGE_LAST3MONTH,
    LoadHistoryRange.RANGE_LAST6MONTH, LoadHistoryRange.RANGE_LASTYEAR];
  */
  const allRanges = [LoadHistoryRange.RANGE_LASTWEEK, LoadHistoryRange.RANGE_LASTMONTH, LoadHistoryRange.RANGE_LAST3MONTH,
    LoadHistoryRange.RANGE_LAST6MONTH, LoadHistoryRange.RANGE_LASTYEAR];

  // filter out the relevant ranges
  const now = moment();
  const startRecording = LoadHistoryStore.getRecordingStart();
  let ranges = [];
  // only when at least 1 part has been fetched, offer a range selector
  if (startRecording) {
    for (const range of allRanges) {
      if (startRecording > _substractRange(now, range)) break;
      ranges.push(range);
    }
    ranges.push(LoadHistoryRange.RANGE_ALL);
  }

  return {
    index: props.index,
    activeTab: props.activeTab,
    language: SettingsStore.getLanguage(),
    userData: LoadHistoryStore.getItems(LoadHistoryDataType.USER_DATA),
    userKeys: LoadHistoryStore.getInfo(LoadHistoryDataType.USER_DATA, 'keys'),
    queueData: LoadHistoryStore.getItems(LoadHistoryDataType.QUEUE_DATA),
    queueKeys: LoadHistoryStore.getInfo(LoadHistoryDataType.QUEUE_DATA, 'keys'),
    isFetchingUserData: LoadHistoryStore.isFetching(LoadHistoryDataType.USER_DATA),
    isFetchingQueueData: LoadHistoryStore.isFetching(LoadHistoryDataType.QUEUE_DATA),
    ranges: Immutable.List(ranges),
    currentRange: LoadHistoryStore.getRange() ?? undefined,
    currentViewport: Immutable.List(LoadHistoryStore.getCurrentViewport()),

    devSettingsEnabled: SettingsStore.getSettings().showExtendedLoadHistory,
    onLoad: (value: number) => {
      const maxSamples: number = Math.max(100, value);
      LoadHistoryActions.setMaxSamples(maxSamples);
      LoadHistoryActions.fetchInitial(LoadHistoryDataType.QUEUE_DATA, maxSamples);
      LoadHistoryActions.fetchInitial(LoadHistoryDataType.USER_DATA, maxSamples);
    },
    onZoomIn: canZoomIn ? handleZoomIn : undefined,
    onZoomOut: canZoomOut ? handleZoomOut : undefined,
    onGoBackward: canGoBackward ? handleGoBackward : undefined,
    onGoForward: canGoForward ? handleGoForward : undefined,
    onGoFastBackward: canGoBackward ? handleGoFastBackward : undefined,
    onGoFastForward: canGoForward ? handleGoFastForward : undefined,
    onSetRange: handleSelectRange,
    onLeave: handleLeave
  };
}

export default FluxUtils.Container.createFunctional(
  asFunctional(LoadHistoryTab),
  getStores,
  calculateState,
  { withProps: true }
);
